import {isNumber} from "radash";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {erIkkeTall, erTall} from "felles-ng2/støttefunksjoner/tall";

/** representerer en enum-type, usikker på om det er mulig å representere denne som noe annet enn 'any' */
type Enum = any;

/** Returnerer navnet på alle innslagene i angitt enum */
export function navnPåAlleEnuminnslag(enumtype: Enum): string[] {
    return erTallbasertEnum(enumtype) ?
      <string[]>Object.values(enumtype).filter(kandidat => erIkkeTall(kandidat)) :
      <string[]>Object.keys(enumtype);
}

/** Returnerer alle innslagene i angitt enum */
export function alleEnuminnslag<E>(enumtype: Enum): E[] {
    // @ts-ignore
    return erTallbasertEnum(enumtype) ?
      <E[]>Object.values(enumtype).filter(kandidat => erTall(kandidat)) :
      <E[]>Object.values(enumtype);
}

/**
 * Returnerer enuminnslag fra angitt enumtype i hht angitt verdi.
 * Dersom angitt verdi ikke representerer noe enuminnslag kastes en exception.
 *
 * <pre>
 * Gitt enumen `enum EnEnum{a,b}`
 * Gir:
 * enumInnslag('a', EnEnum) == EnEnum.a
 * enumInnslag(0, EnEnum) == EnEnum.a
 * </pre>
 */
export function enumInnslag<E>(verdi: any, enumtype: Enum): E {
    return erTallbasertEnum(enumtype) ?
      tallbasertEnumInnslag(verdi, enumtype) :
      strengbasertEnumInnslag(verdi, enumtype);
}

/**
 * Returnerer navnet på angitt innslag i angitt enum, for debug-bruk
 * <pre>
 * Gitt enumen `enum EnEnum{a,b}`
 * Gir:
 * enuminnsalgSomStreng(EnEnum.a, EnEnum) == 'a'
 * </pre>
 */
export function navnPåEnuminnslag(verdi: any, enumtype: Enum): string {
    if (erNil(verdi)) return 'null';
    return erTallbasertEnum(enumtype) ?
      enumtype[verdi] :
      verdi;
}

/** Returnerer en streng med enumdefinisjonen, for debug-bruk */
export function enumSomStreng(enumtype: Enum): string {
    const innslag = Object.entries(enumtype)
      .map(innslag => `${innslag[0]}(${innslag[1]})`)
      .join(', ');
    return `enum[${innslag}]`
}

export function erTallbasertEnum(enumtype: Enum) {
    //baserer seg på at tallbaserte enumer (enum {a, b}) kan sees på som en array med ['a':0, 'b':', '0': 'a', '1': 'b']
    //men en strengbaert enum (enum {a='x', b='y'}) kun har innslag for enum-navnene: ['a':'x', 'b':'y']
    return Object.values(enumtype).some(innslag => isNumber(innslag))
}

/**
 * En funksjon som ikke gjør annet enn å sørge for kompileringsfeil dersom ikke alle enum-innslag er inkludert i et switch-uttrykk:
 *
 * Bruk:
 * ```
 * enum E {a,b,c}
 * const e:E ...
 * switch (e){
 *  case E.a: ...
 *  case E.b: ...
 *  case E.c: ...
 *  default: caseForEnumMangler(e) <-- denne vil sørge for kompileringsfeil dersom ikke all innslag i E er behandlet i switch (kun i dette tilfelle vil angitt e være never)
 * }
 * ```
 */
export function caseForEnumMangler<E>(value: never): E {
    throw new Error(`case for enumverdi '${value}' mangler`);
}

export function caseForEnumManglerIkkeKastException<E>(value: never): E {
    return value;
}

//hjelpemetoder

function tallbasertEnumInnslag<E>(verdi: any, enumtype: Enum): E {
    const enumVerdi = isNaN(verdi) ? enumtype[verdi] : parseInt(verdi);
    if (erNil(enumVerdi) || isNaN(enumVerdi)) throw new Error(`ugyldig enumverdi '${verdi}' angitt for tallbasert enum ${enumSomStreng(enumtype)}`)
    return enumVerdi;
}

function strengbasertEnumInnslag<E>(verdi: any, enumtype: Enum): E {
    const enumVerdi = enumtype[verdi];
    if (erNil(enumVerdi)) throw new Error(`ugyldig enumverdi '${verdi}' angitt for streng-basert enum ${enumSomStreng(enumtype)}`)
    return enumVerdi;
}
